import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listAdminFailedScheduledTasks, retryAdminScheduledTask } from '../../admin/adminApi'
import type { AdminFailedScheduledTask } from '../../admin/types'
import { Button, Card, I } from '../../components/ui'
import { CodeValue, EmptyState, ErrorState, LoadingState } from './adminPageHelpers'
import { formatDateTime, tableContainerStyle, tdStyle, thStyle } from './adminPageFormatters'

interface AdminScheduledTasksPageProps {
  accessToken: string
  onOpenStockDiagnostics?: () => void
}

export function AdminScheduledTasksPage({ accessToken, onOpenStockDiagnostics }: AdminScheduledTasksPageProps) {
  const { t, i18n } = useTranslation()
  const [tasks, setTasks] = useState<AdminFailedScheduledTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retrying, setRetrying] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const load = useCallback(function load() {
    setLoading(true)
    setError(false)
    listAdminFailedScheduledTasks(accessToken)
      .then(setTasks)
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [accessToken])

  useEffect(() => {
    void Promise.resolve().then(load)
  }, [load])

  async function retryTask(task: AdminFailedScheduledTask) {
    const confirmed = window.confirm(t('admin.scheduledTasks.retry.confirm', { taskName: task.taskName, instanceId: task.instanceId }))
    if (!confirmed) return
    const key = `${task.taskName}:${task.instanceId}`
    setRetrying(key)
    setMessage(null)
    try {
      await retryAdminScheduledTask(accessToken, task.taskName, task.instanceId)
      setMessage(t('admin.scheduledTasks.retry.accepted'))
      await listAdminFailedScheduledTasks(accessToken).then(setTasks)
    } catch {
      setMessage(t('admin.scheduledTasks.retry.failed'))
    } finally {
      setRetrying(null)
    }
  }

  if (loading) return <LoadingState label={t('admin.scheduledTasks.loading')} />
  if (error) return <ErrorState title={t('admin.scheduledTasks.error.title')} body={t('admin.scheduledTasks.error.body')} onRetry={load} />

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--brand-900)', fontSize: 20 }}>{t('admin.scheduledTasks.title')}</h2>
            <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 14 }}>{t('admin.scheduledTasks.subtitle')}</div>
          </div>
          <Button kind="secondary" icon={I.refresh} onClick={load}>{t('admin.common.refresh')}</Button>
        </div>
        {message && <div role="status" style={{ marginTop: 12, color: 'var(--brand-700)', fontWeight: 800 }}>{message}</div>}
      </Card>

      {tasks.length === 0 ? (
        <EmptyState title={t('admin.scheduledTasks.empty.title')} body={t('admin.scheduledTasks.empty.body')} />
      ) : (
        <div style={tableContainerStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>{t('admin.scheduledTasks.table.task')}</th>
                <th style={thStyle}>{t('admin.scheduledTasks.table.instance')}</th>
                <th style={thStyle}>{t('admin.scheduledTasks.table.lastFailure')}</th>
                <th style={thStyle}>{t('admin.scheduledTasks.table.executionTime')}</th>
                <th style={thStyle}>{t('admin.scheduledTasks.table.failures')}</th>
                <th style={thStyle}>{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(task => {
                const key = `${task.taskName}:${task.instanceId}`
                return (
                  <tr key={key}>
                    <td style={{ ...tdStyle, color: 'var(--fg)', fontWeight: 800 }}><CodeValue>{task.taskName}</CodeValue></td>
                    <td style={tdStyle}><CodeValue>{task.instanceId}</CodeValue></td>
                    <td style={tdStyle}>{formatDateTime(task.lastFailure, i18n.language, t('admin.common.none'))}</td>
                    <td style={tdStyle}>{formatDateTime(task.executionTime, i18n.language, t('admin.common.none'))}</td>
                    <td style={tdStyle}>{task.consecutiveFailures ?? 0}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {onOpenStockDiagnostics && task.taskName === 'channel.stock-allocation-push' && <Button kind="secondary" size="sm" onClick={onOpenStockDiagnostics}>{t('admin.scheduledTasks.diagnostics')}</Button>}
                        <Button kind="danger" size="sm" onClick={() => retryTask(task)} disabled={retrying === key}>
                          {retrying === key ? t('admin.scheduledTasks.retry.retrying') : t('admin.scheduledTasks.retry.action')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
